import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';

type ToastType = 'success' | 'error' | 'info' | 'warning';
type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  animations: [
    trigger('toastFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(16px) translateY(4px) scale(0.96)' }),
        animate(
          '260ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          style({ opacity: 1, transform: 'translateX(0) translateY(0) scale(1)' })
        )
      ]),
      transition(':leave', [
        animate(
          '180ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({ opacity: 0, transform: 'translateX(8px) translateY(2px) scale(0.98)' })
        )
      ])
    ])
  ],
  template: `
    @if (visible) {
      <div
        [ngClass]="positionClasses"
        class="pointer-events-auto"
        @toastFade
      >
        <div
          class="flex items-center gap-3 rounded-xl px-5 py-4 shadow-lg backdrop-blur-lg w-full max-w-md"
          [ngClass]="typeClasses"
        >
          <div class="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-opacity-20" [ngClass]="{
            'bg-green-500 text-green-500': type === 'success',
            'bg-red-500 text-red-500': type === 'error',
            'bg-amber-500 text-amber-500': type === 'warning',
            'bg-blue-500 text-blue-500': type === 'info'
          }">
            <ng-container [ngSwitch]="type">
              <svg *ngSwitchCase="'success'" class="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9.00039 16.2002L4.80039 12.0002L3.40039 13.4002L9.00039 19.0002L21.0004 7.0002L19.6004 5.6002L9.00039 16.2002Z"
                  fill="currentColor"
                />
              </svg>
              <svg *ngSwitchCase="'error'" class="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Zm0-11.5a1 1 0 0 0-1 1V16a1 1 0 1 0 2 0v-4.5a1 1 0 0 0-1-1Zm0-3.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z"
                  fill="currentColor"
                />
              </svg>
              <svg *ngSwitchCase="'warning'" class="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12.866 3.5c-.321-.553-.9-.894-1.532-.894s-1.21.341-1.532.894L2.4 16.25C1.99 16.958 1.982 17.82 2.38 18.535 2.777 19.25 3.52 19.7 4.32 19.7h15.36c.8 0 1.543-.45 1.94-1.165.398-.715.39-1.577-.02-2.285L12.866 3.5ZM11 9.25c0-.414.336-.75.75-.75h.5c.414 0 .75.336.75.75V14c0 .414-.336.75-.75.75h-.5A.75.75 0 0 1 11 14V9.25Zm1.5 7.75a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z"
                  fill="currentColor"
                />
              </svg>
              <svg *ngSwitchDefault class="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm.75 5.5a.75.75 0 0 0-1.5 0v6a.75.75 0 0 0 1.5 0v-6Zm-1.25 9.75a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
                  fill="currentColor"
                />
              </svg>
            </ng-container>
          </div>

          <div class="flex flex-col gap-0.5 min-w-[220px] max-w-sm">
            @if (title) {
              <span class="text-sm font-semibold tracking-wide">{{ title }}</span>
            }
            @if (message) {
              <span class="text-sm leading-snug">{{ message }}</span>
            }
          </div>

          @if (dismissible) {
            <button
              type="button"
              class="ml-auto px-3 py-1.5 text-xs font-medium rounded-md transition-colors" [ngClass]="{
                'text-green-700 hover:bg-green-100 dark:text-green-200 dark:hover:bg-green-800/50': type === 'success',
                'text-red-700 hover:bg-red-100 dark:text-red-200 dark:hover:bg-red-800/50': type === 'error',
                'text-amber-700 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-800/50': type === 'warning',
                'text-blue-700 hover:bg-blue-100 dark:text-blue-200 dark:hover:bg-blue-800/50': type === 'info'
              }"
              (click)="close()"
            >
              Fermer
            </button>
          }
        </div>
      </div>
    }
  `
})
export class ToastComponent implements OnChanges, OnDestroy {
  @Input() type: ToastType = 'info';
  @Input() position: ToastPosition = 'bottom-right';
  @Input() title = '';
  @Input() message = '';
  @Input() visible = false;
  @Input() autoHide = true;
  @Input() autoHideMs = 4000;
  @Input() dismissible = true;

  @Output() visibleChange = new EventEmitter<boolean>();

  private hideTimeoutId: number | null = null;

  get typeClasses(): string {
    const baseClasses = 'border-l-4';
    switch (this.type) {
      case 'success':
        return `${baseClasses} bg-green-50 dark:bg-green-900/80 border-green-500 text-green-800 dark:text-green-100`;
      case 'error':
        return `${baseClasses} bg-red-50 dark:bg-red-900/80 border-red-500 text-red-800 dark:text-red-100`;
      case 'warning':
        return `${baseClasses} bg-amber-50 dark:bg-amber-900/80 border-amber-500 text-amber-800 dark:text-amber-100`;
      default:
        return `${baseClasses} bg-blue-50 dark:bg-slate-800/90 border-blue-500 text-slate-800 dark:text-slate-100`;
    }
  }

  get positionClasses(): string {
    switch (this.position) {
      case 'top-right':
        return 'fixed top-28 right-6 z-[9999]';
      case 'top-left':
        return 'fixed top-28 left-6 z-[9999]';
      case 'bottom-left':
        return 'fixed bottom-6 left-6 z-[9999]';
      default:
        return 'fixed bottom-6 right-6 z-[9999]';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue) {
      this.startAutoHideTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearAutoHideTimer();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.clearAutoHideTimer();
  }

  private startAutoHideTimer(): void {
    this.clearAutoHideTimer();
    if (!this.autoHide) return;

    this.hideTimeoutId = window.setTimeout(() => {
      this.close();
    }, this.autoHideMs);
  }

  private clearAutoHideTimer(): void {
    if (this.hideTimeoutId !== null) {
      window.clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }
}


